/**
 * Getting photos off the web app and into the phone's camera roll.
 *
 * There is exactly one route, and it is not an API that writes to the gallery —
 * no such API exists on the web. It is `navigator.share({files})`, which hands
 * the files to the OS share sheet; on iOS that sheet offers "Save Image", which
 * is what actually lands them in Photos. Everything else — `<a download>`, a
 * ZIP — puts files in a *file system* (Files on iOS, Downloads on Android), not
 * the gallery.
 *
 * Consequences that shape this module:
 *
 *  - **`share()` consumes transient activation**, so it must be called with no
 *    `await` in front of it and cannot be looped from a single tap. Bytes are
 *    fetched in a separate, earlier step; the tap only shares what is already
 *    in memory. Getting this wrong is the classic "works on desktop, fails on
 *    iPhone".
 *  - **Chromium caps a share at 10 files and 50 MB.** Exceeding either rejects
 *    with "Share too large", so batches are capped below both.
 *  - **iOS wants `files` and nothing else.** Adding `title`/`text`/`url` makes
 *    file sharing fail there, so this never sends them.
 *  - **We cannot know what the user picked.** `share()` resolves without
 *    reporting the target, so nothing here claims the photos were saved.
 */

/** Chromium's hard limits. Both are enforced below, with headroom. */
export const SHARE_MAX_FILES = 10;
const SHARE_MAX_BYTES = 45 * 1024 * 1024;

/**
 * A 1×1 JPEG, used to ask the browser whether it will share *image files* —
 * not merely whether `canShare` exists. Firefox for Android implements
 * `canShare` but not file sharing, and answers `false` to this, which is
 * exactly the gate we want.
 */
const PROBE =
  "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/wAALCAABAAEBAREA/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/2gAIAQEAAD8AKp//2Q==";

/**
 * The right route differs by platform, and not in the way you would guess.
 *
 *  - **iOS**: the share sheet is the only way in. Its "Save Image" action writes
 *    to Photos. A download goes to the Files app instead, which is useless for
 *    this, and iOS only handles the *first* of several downloads anyway.
 *
 *  - **Android**: the opposite. There is no stock "save to gallery" share
 *    target — AOSP's file manager registers no `ACTION_SEND` filter at all, so
 *    what appears in the sheet is whatever apps happen to be installed. But a
 *    plain download *is* indexed: Android's MediaProvider derives `media_type`
 *    from the file extension regardless of the folder, so a `.jpg` in
 *    `Download/` lands in `MediaStore.Images` and shows up in Google Photos'
 *    device folders and Samsung Gallery's Downloads album. Downloading is the
 *    reliable path, and the filename extension is what makes it work.
 */
export type SaveStrategy = "share" | "download" | "longpress";

/**
 * Whether the share sheet is available for image files.
 *
 * Cached: the answer cannot change within a page load, and building the probe
 * file on every render is wasteful.
 */
let shareSupport: boolean | null = null;

export function canShareFiles(): boolean {
  if (shareSupport !== null) return shareSupport;

  try {
    if (
      typeof navigator.canShare !== "function" ||
      typeof navigator.share !== "function"
    ) {
      shareSupport = false;
      return false;
    }
    const bytes = Uint8Array.from(atob(PROBE.split(",")[1]!), (c) =>
      c.charCodeAt(0),
    );
    const probe = new File([bytes], "probe.jpg", { type: "image/jpeg" });
    shareSupport = navigator.canShare({ files: [probe] });
  } catch {
    shareSupport = false;
  }
  return shareSupport;
}

export function saveStrategy(): SaveStrategy {
  if (isApplePhone()) {
    // Downloading on iOS lands in Files, never Photos, so if the sheet is
    // unavailable the honest answer is to tell the user to long-press.
    return canShareFiles() ? "share" : "longpress";
  }
  // Everywhere else a download is both available and actually indexed.
  return "download";
}

/** Rough iOS detection, used only to choose wording — never to gate behaviour. */
export function isApplePhone(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac; the touch points give it away.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export interface DownloadableFile {
  fileId: string;
  name: string;
  url: string;
}

/**
 * Split files into batches the platform will actually accept.
 *
 * Sizes are only known after fetching, so this batches on count and on the
 * server-reported size when we have one; `fetchAsFiles` re-checks the real
 * byte count and will not overfill a batch.
 */
export function batchForSharing<T extends { sizeBytes?: number }>(
  items: T[],
): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let bytes = 0;

  for (const item of items) {
    const size = item.sizeBytes ?? 0;
    const wouldOverflow =
      current.length >= SHARE_MAX_FILES ||
      (bytes + size > SHARE_MAX_BYTES && current.length > 0);
    if (wouldOverflow) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += size;
  }

  if (current.length > 0) batches.push(current);
  return batches;
}

/**
 * Download the bytes so a later tap can share them synchronously.
 *
 * `File`, not `Blob`: the share sheet uses the name, and an unnamed blob shows
 * up in Photos as an untitled item.
 */
export async function fetchAsFiles(
  items: DownloadableFile[],
  onProgress?: (done: number, total: number) => void,
): Promise<File[]> {
  const files: File[] = [];

  for (const [index, item] of items.entries()) {
    const response = await fetch(item.url);
    if (!response.ok) throw new Error(`Couldn't fetch ${item.name}`);
    const blob = await response.blob();

    files.push(
      new File([blob], item.name, {
        // The server's content type is authoritative; a name ending in .jpg is
        // not a guarantee of what the bytes are.
        type: blob.type || "image/jpeg",
        lastModified: Date.now(),
      }),
    );
    onProgress?.(index + 1, items.length);
  }

  return files;
}

/**
 * Save files to the device's download folder, one at a time.
 *
 * Two details carry the whole feature:
 *
 *  - **The bytes are fetched and re-served as a `blob:` URL.** The `download`
 *    attribute is honoured only for same-origin, `blob:` and `data:` URLs — on
 *    a cross-origin URL it is silently ignored and the browser navigates to the
 *    image instead of saving it. Our media comes from a different origin than
 *    the app, so linking straight to the signed URL would quietly do nothing.
 *
 *  - **The filename must keep a real image extension.** Android decides whether
 *    a downloaded file is a photo by extension, not by `Content-Type`. A file
 *    saved without one is classified as "not media" and never reaches the
 *    gallery, no matter how correct the HTTP headers were.
 *
 * Sequential, with a gap between saves: browsers throttle or block rapid-fire
 * downloads, and a burst of them is indistinguishable from an abusive page.
 */
export async function downloadFiles(
  items: DownloadableFile[],
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  let saved = 0;

  for (const item of items) {
    try {
      const response = await fetch(item.url);
      if (!response.ok) continue;
      const blob = await response.blob();

      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = withImageExtension(item.name, blob.type);
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoked on a later turn: revoking synchronously can cancel the save
      // before the browser has read the blob.
      window.setTimeout(() => URL.revokeObjectURL(href), 30_000);

      saved += 1;
      onProgress?.(saved, items.length);
      await new Promise((resolve) => setTimeout(resolve, 350));
    } catch {
      // One unreachable file should not abandon the rest of the album.
    }
  }

  return saved;
}

const EXTENSION_FOR: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/avif": ".avif",
  "image/gif": ".gif",
  "video/mp4": ".mp4",
};

/** Guarantees the name ends in something the OS will recognise as media. */
export function withImageExtension(name: string, mime: string): string {
  const wanted = EXTENSION_FOR[mime];
  if (!wanted) return name;
  return name.toLowerCase().endsWith(wanted)
    ? name
    : `${stripExtension(name)}${wanted}`;
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  // Only treat a trailing dot-group as an extension; "Trip 2.0 photos" is a name.
  return dot > 0 && name.length - dot <= 6 ? name.slice(0, dot) : name;
}

export type ShareOutcome = "shared" | "cancelled" | "unsupported" | "failed";

/**
 * Hand pre-fetched files to the OS.
 *
 * MUST be called directly from a user gesture with no `await` before it. The
 * files argument is already-resolved data for exactly this reason.
 */
export async function shareFiles(files: File[]): Promise<ShareOutcome> {
  if (files.length === 0) return "failed";
  if (typeof navigator.share !== "function") return "unsupported";
  if (
    typeof navigator.canShare === "function" &&
    !navigator.canShare({ files })
  ) {
    return "unsupported";
  }

  try {
    // `files` alone: any other property makes this fail on iOS.
    await navigator.share({ files });
    // Resolution means the sheet closed, not that anything was saved — the API
    // never reports the chosen target. Callers must not claim success.
    return "shared";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      return "cancelled";
    return "failed";
  }
}
